/**
 * SymbolSelector - Dynamic Symbol Tabs Component
 * 
 * Renders dynamic tabs based on active engines from the Index Master.
 * No hardcoded symbols - everything is fetched from the backend.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Activity, TrendingUp, AlertCircle, RefreshCw, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SymbolSelector = ({ 
  currentSymbol, 
  onSymbolChange, 
  token,
  className = "" 
}) => {
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch active engines from backend
  const fetchEngines = useCallback(async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/engines/status`, { headers });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      const data = await res.json();
      
      if (data.engines && data.engines.length > 0) {
        setEngines(data.engines);
        setError(null);
        
        // If current symbol is not in active engines, switch to first active
        const activeSymbols = data.engines.map(e => e.symbol);
        if (!activeSymbols.includes(currentSymbol) && activeSymbols.length > 0) {
          onSymbolChange(activeSymbols[0]);
        }
      } else {
        // No active engines - show default
        setEngines([{ symbol: 'NIFTY', is_running: false, is_expiry_day: false }]);
      }
    } catch (err) {
      console.error('Failed to fetch engines:', err);
      setError(err.message);
      // Fallback to default
      setEngines([{ symbol: 'NIFTY', is_running: false }]);
    } finally {
      setLoading(false);
    }
  }, [token, currentSymbol, onSymbolChange]);

  useEffect(() => {
    fetchEngines();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchEngines, 30000);
    return () => clearInterval(interval);
  }, [fetchEngines]);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <RefreshCw className="w-4 h-4 animate-spin text-accent" />
        <span className="text-sm text-muted-foreground">Loading symbols...</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="symbol-selector">
      {engines.map((engine) => {
        const isActive = engine.symbol === currentSymbol;
        const isExpiry = engine.is_expiry_day;
        const isRunning = engine.is_running;
        
        return (
          <button
            key={engine.symbol}
            onClick={() => onSymbolChange(engine.symbol)}
            data-testid={`symbol-tab-${engine.symbol.toLowerCase()}`}
            className={`
              relative px-4 py-2 rounded-lg font-medium text-sm
              transition-all duration-200 flex items-center gap-2
              ${isActive 
                ? 'bg-accent text-accent-foreground shadow-lg shadow-accent/20' 
                : 'bg-surface hover:bg-surface-hover text-muted-foreground hover:text-foreground'
              }
              ${isExpiry ? 'ring-2 ring-amber-400/50' : ''}
            `}
          >
            {/* Running indicator */}
            {isRunning && (
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isActive ? 'bg-accent-foreground' : 'bg-buy'} opacity-75`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? 'bg-accent-foreground' : 'bg-buy'}`}></span>
              </span>
            )}
            
            {/* Symbol name */}
            <span>{engine.symbol}</span>
            
            {/* Expiry badge */}
            {isExpiry && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-400/20 text-amber-400 rounded uppercase">
                EXP
              </span>
            )}
            
            {/* Not running indicator */}
            {!isRunning && (
              <AlertCircle className="w-3 h-3 text-muted-foreground" />
            )}
          </button>
        );
      })}
      
      {/* Refresh button */}
      <button
        onClick={fetchEngines}
        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
        title="Refresh symbols"
      >
        <RefreshCw className="w-4 h-4" />
      </button>
      
      {/* Error indicator */}
      {error && (
        <span className="text-xs text-sell ml-2" title={error}>
          <AlertCircle className="w-4 h-4 inline" />
        </span>
      )}
    </div>
  );
};


/**
 * EngineStatusBar - Shows status of all running engines
 */
const EngineStatusBar = ({ token }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(`${API}/api/engines/metrics`, { headers });
        const data = await res.json();
        setMetrics(data);
      } catch (err) {
        console.error('Failed to fetch engine metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 10000);
    return () => clearInterval(interval);
  }, [token]);

  if (loading || !metrics) return null;

  const engineCount = metrics.active_engines || 0;
  const engines = metrics.engines || {};

  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground" data-testid="engine-status-bar">
      <div className="flex items-center gap-1.5">
        <Activity className="w-3.5 h-3.5 text-accent" />
        <span>{engineCount} Engine{engineCount !== 1 ? 's' : ''}</span>
      </div>
      
      {Object.entries(engines).map(([symbol, engine]) => (
        <div 
          key={symbol} 
          className="flex items-center gap-1.5 px-2 py-1 bg-surface rounded"
          title={`${symbol}: ${engine.ticks_processed} ticks, ${engine.signals_generated} signals`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${engine.is_running ? 'bg-buy' : 'bg-sell'}`} />
          <span className="font-mono">{symbol}</span>
          {engine.config?.is_expiry_day && (
            <Zap className="w-3 h-3 text-amber-400" />
          )}
        </div>
      ))}
    </div>
  );
};


export { SymbolSelector, EngineStatusBar };
export default SymbolSelector;
